import { useMemo } from "react";
import { useQuery, type ApolloError } from "@apollo/client";
import AiCard from "@/components/AiCard";
import useDebouncedValue from "@/hooks/useDebouncedValue";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import { sendToRaygun } from "@/lib/raygunUtils";
import { toMarkdownBulletList } from "../../_utils";
import { CAREER_SERVICES_INTERACTION_HIGHLIGHTS_QUERY } from "../queries";

const HIGHLIGHTS_DEBOUNCE_MS = 600;
const HIGHLIGHTS_UNAVAILABLE = "Highlights unavailable right now.";

type Props = {
  startDateTime: string;
  endDateTime: string;
  categories: string[];
  mode: string | null;
  ownerId: string | null;
  enabled: boolean;
};

export default function CareerHighlights({ startDateTime, endDateTime, categories, mode, ownerId, enabled }: Props) {
  const filters = useMemo(
    () => ({ startDateTime, endDateTime, categories: [...categories].sort(), mode, ownerId }),
    [startDateTime, endDateTime, categories, mode, ownerId]
  );
  const debouncedFilters = useDebouncedValue(filters, HIGHLIGHTS_DEBOUNCE_MS);

  const { data, previousData, loading } = useQuery(CAREER_SERVICES_INTERACTION_HIGHLIGHTS_QUERY, {
    variables: {
      startDateTime: debouncedFilters.startDateTime,
      endDateTime: debouncedFilters.endDateTime,
      categories: debouncedFilters.categories,
      mode: debouncedFilters.mode,
      ownerId: debouncedFilters.ownerId
    },
    // Empty categories match nothing everywhere (see filterCareerServicesStats).
    skip: !enabled || debouncedFilters.categories.length === 0,
    notifyOnNetworkStatusChange: true,
    onError: (queryError: ApolloError) => {
      sendToRaygun(queryError, { tags: ["career-services", "highlights"] });
    }
  });

  // Drop stale briefs when skipped (e.g. every category cleared); keep previous
  // data only while a refetch for a non-empty filter is in flight.
  const skipped = !enabled || debouncedFilters.categories.length === 0;
  const highlights = skipped ? undefined : (data ?? previousData)?.careerServicesInteractionHighlights;
  const busy = loading && !highlights;
  const bullets = highlights?.bullets ?? [];

  return (
    <div data-testid="career-services-highlights">
      <AiCard
        title="Highlights"
        text={bullets.length > 0 ? toMarkdownBulletList(bullets) : HIGHLIGHTS_UNAVAILABLE}
        loading={busy}
        renderAsMarkdown
      />
      {highlights?.generatedAt && (
        <p
          className="text-body-medium text-sys-brand-on-surface-variant mt-4"
          data-testid="career-services-highlights-freshness"
        >
          Updated {toDateTimeFormat(highlights.generatedAt)}
        </p>
      )}
    </div>
  );
}
